import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PartyPopper, Sparkles, Loader2, Rocket } from "lucide-react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { buildProfileContext } from "../lib/profile";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Markdown } from "../components/Markdown";

const BUDGETS = ["Moins de 100€", "100€ - 300€", "300€ - 800€", "Plus de 800€"];
const GOALS = ["Attirer de nouveaux clients", "Fidéliser", "Écouler du stock", "Lancer un produit", "Créer du buzz local"];

export default function EventLab() {
  const nav = useNavigate();
  const { data: appData, launchPlan } = useApp();
  const [f, setF] = useState({ sector: "Restaurant / Café", budget: "100€ - 300€", goal: "Attirer de nouveaux clients", date: "", audience: "" });
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);

  const generate = async () => {
    setLoading(true); setOut("");
    try {
      const { data } = await api.post("/ai/event", { ...f, profile_context: buildProfileContext(appData) });
      setOut(data.content);
      toast.success("Animation générée !");
    } catch { toast.error("Erreur de génération"); }
    finally { setLoading(false); }
  };

  const launchAsPlan = async () => {
    if (launching) return;
    setLaunching(true);
    try {
      const message = `Animation commerciale « ${f.goal} » pour un ${f.sector}. Budget: ${f.budget}. Public cible: ${f.audience || "clientèle locale"}. Date: ${f.date || "à définir"}. Crée les missions de préparation, communication (publications, story, message), l'animation le jour J et la mesure des résultats.`;
      const { data: plan } = await api.post("/ai/plan", { message, context: out.slice(0, 1500), profile_context: buildProfileContext(appData) });
      const { duplicate } = launchPlan({ ...plan, key: `event ${f.goal} ${f.sector} ${f.date}` });
      if (duplicate) toast("Plan déjà actif", { description: "Cette animation a déjà été lancée.", action: { label: "Voir le plan", onClick: () => nav("/app/dashboard") } });
      else toast.success(`${plan.missions.length} missions créées !`, { description: "Ajoutées au calendrier et au tableau de bord.", action: { label: "Voir le plan", onClick: () => nav("/app/dashboard") } });
    } catch { toast.error("Impossible de générer le plan. Réessayez."); }
    finally { setLaunching(false); }
  };


  return (
    <Layout>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center text-neon"><PartyPopper size={22} /></div>
        <div><h1 className="font-display font-black text-2xl lg:text-3xl">Event Lab</h1><p className="text-sm text-[hsl(var(--muted-foreground))]">Générez une animation commerciale complète.</p></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="lb-card p-5 space-y-4 h-fit" data-testid="event-form">
          <div><Label className="text-xs">Secteur</Label><Input data-testid="event-sector" value={f.sector} onChange={(e) => setF({ ...f, sector: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
          <div><Label className="text-xs">Budget</Label>
            <Select value={f.budget} onValueChange={(v) => setF({ ...f, budget: v })}><SelectTrigger data-testid="event-budget" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue /></SelectTrigger><SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Objectif</Label>
            <Select value={f.goal} onValueChange={(v) => setF({ ...f, goal: v })}><SelectTrigger data-testid="event-goal" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue /></SelectTrigger><SelectContent>{GOALS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><Input data-testid="event-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
            <div><Label className="text-xs">Public cible</Label><Input data-testid="event-audience" value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} placeholder="Familles, étudiants…" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
          </div>
          <Button data-testid="event-generate" onClick={generate} disabled={loading} className="w-full rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <><Sparkles size={16} className="mr-1" /> Générer l'animation</>}
          </Button>
        </div>
        <div className="lb-card p-5" data-testid="event-output">
          <Label className="text-xs mb-2 block">Concept généré</Label>
          <div className="min-h-[300px] rounded-xl bg-white/[0.02] border border-white/5 p-4 text-sm overflow-y-auto max-h-[70vh]">
            {loading ? <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="skeleton h-4" style={{ width: `${70 + (i % 3) * 10}%` }} />)}</div>
              : out ? <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}><Markdown>{out}</Markdown></motion.div>
              : <p className="text-[hsl(var(--muted-foreground))]">Renseignez les critères puis générez votre animation : concept, déroulement, matériel, communication, publications, budget et indicateurs.</p>}
          </div>
          {out && !loading && (
            <Button data-testid="event-launch-plan" onClick={launchAsPlan} disabled={launching}
              className="w-full mt-4 rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
              {launching ? <Loader2 className="animate-spin" size={18} /> : <><Rocket size={16} className="mr-1.5" /> Lancer le plan</>}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
